import React, {
  useMemo,
  useState,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useImperativeHandle,
} from "react";

import StateSidebar from "./zoneComponent/StateSidebar";
import ZoneCard from "./zoneComponent/ZoneCard";
import IndiaMap from "./zoneComponent/IndiaMap";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  Tooltip,
} from "@mui/material";
import AddEditZoneDialog from "./zoneComponent/AddEditZoneDialog";
import Swal from "sweetalert2";
import { InfoOutlined } from "@mui/icons-material";
import GlobalButton from "../utils/GlobalButton";

// const initialStates = [
//   {
//     id: 1,
//     name: "Punjab",
//     lat: 31.1471,
//     lng: 75.3412,
//     zoneId: 1,
//   },
//   {
//     id: 2,
//     name: "Haryana",
//     lat: 29.0588,
//     lng: 76.0856,
//     zoneId: 1,
//   },
//   {
//     id: 3,
//     name: "Delhi",
//     lat: 28.6139,
//     lng: 77.209,
//     zoneId: null,
//   },
//   {
//     id: 4,
//     name: "Rajasthan",
//     lat: 27.0238,
//     lng: 74.2179,
//     zoneId: null,
//   },
//   {
//     id: 5,
//     name: "Gujarat",
//     lat: 22.2587,
//     lng: 71.1924,
//     zoneId: 4,
//   },
// ];

const initialStates = [
  { id: 1, name: "Andhra Pradesh" },
  { id: 2, name: "Arunachal Pradesh" },
  { id: 3, name: "Assam" },
  { id: 4, name: "Bihar" },
  { id: 5, name: "Chhattisgarh" },
  { id: 6, name: "Goa" },
  { id: 7, name: "Gujarat" },
  { id: 8, name: "Haryana" },
  { id: 9, name: "Himachal Pradesh" },
  { id: 10, name: "Jharkhand" },
  { id: 11, name: "Karnataka" },
  { id: 12, name: "Kerala" },
  { id: 13, name: "Madhya Pradesh" },
  { id: 14, name: "Maharashtra" },
  { id: 15, name: "Manipur" },
  { id: 16, name: "Meghalaya" },
  { id: 17, name: "Mizoram" },
  { id: 18, name: "Nagaland" },
  { id: 19, name: "Odisha" },
  { id: 20, name: "Punjab" },
  { id: 21, name: "Rajasthan" },
  { id: 22, name: "Sikkim" },
  { id: 23, name: "Tamil Nadu" },
  { id: 24, name: "Telangana" },
  { id: 25, name: "Tripura" },
  { id: 26, name: "Uttar Pradesh" },
  { id: 27, name: "Uttarakhand" },
  { id: 28, name: "West Bengal" },

  // Union Territories
  { id: 29, name: "Andaman and Nicobar Islands" },
  { id: 30, name: "Chandigarh" },
  { id: 31, name: "Dadra and Nagar Haveli and Daman and Diu" },
  { id: 32, name: "Delhi" },
  { id: 33, name: "Jammu and Kashmir" },
  { id: 34, name: "Ladakh" },
  { id: 35, name: "Lakshadweep" },
  { id: 36, name: "Puducherry" },
];

const defaultZoneSetup = [
  {
    name: "North Zone",
    color: "#ffd591",
    states: [
      "Chandigarh",
      "Delhi",
      "Haryana",
      "Himachal Pradesh",
      "Punjab",
      "Rajasthan",
      "Uttar Pradesh",
      "Uttarakhand",
      "Jammu and Kashmir",
      "Ladakh",
    ],
  },
  {
    name: "South Zone",
    color: "#b5f5ec",
    states: [
      "Andhra Pradesh",
      "Karnataka",
      "Kerala",
      "Tamil Nadu",
      "Telangana",
      "Puducherry",
      "Lakshadweep",
      "Andaman and Nicobar Islands",
    ],
  },
  {
    name: "East Zone",
    color: "#ffa39e",
    states: ["Bihar", "Jharkhand", "Odisha", "West Bengal"],
  },
  {
    name: "West Zone",
    color: "#91d5ff",
    states: [
      "Goa",
      "Gujarat",
      "Maharashtra",
      "Dadra and Nagar Haveli and Daman and Diu",
    ],
  },
  {
    name: "Central Zone",
    color: "#b7eb8f",
    states: ["Chhattisgarh", "Madhya Pradesh"],
  },
  {
    name: "North East Zone",
    color: "#d3adf7",
    states: [
      "Arunachal Pradesh",
      "Assam",
      "Manipur",
      "Meghalaya",
      "Mizoram",
      "Nagaland",
      "Sikkim",
      "Tripura",
    ],
  },
];

const DEFAULT_ZONE_PREFIX = "default-zone-";

const isMongoId = (id) => /^[a-f\d]{24}$/i.test(String(id));

export const buildRegionPayload = (zones = [], states = []) =>
  zones.map((zone) => ({
    ...(isMongoId(zone.id) ? { _id: zone.id } : {}),
    regionName: zone.name,
    color: zone.color,
    states: states
      .filter((state) => state.zoneId === zone.id)
      .map((state) => state.name),
  }));

const ZoneManagement = forwardRef(
  ({ data, onChange, loading = false, fetchRegions, fetchKey }, ref) => {
    console.log(data);
    const [zones, setZones] = useState(data?.zones || []);

    const [states, setStates] = useState(data?.states || initialStates);

    const [search, setSearch] = useState("");

    const [selectedState, setSelectedState] = useState(null);
    const [openZoneDialog, setOpenZoneDialog] = useState(false);
    const [editingZone, setEditingZone] = useState(null);

    const [isFetching, setIsFetching] = useState(
      typeof fetchRegions === "function",
    );
    const fetchRef = useRef(fetchRegions);
    fetchRef.current = fetchRegions;
    const applySavedRegions = (regions) => {
      const mappedZones = regions.map((item) => ({
        id: item._id,
        name: item.regionName,
        color: item.color,
      }));

      const mappedStates = initialStates.map((state) => {
        const region = regions.find((r) =>
          (r.states || []).includes(state.name),
        );

        return {
          ...state,
          zoneId: region?._id || null,
        };
      });

      setZones(mappedZones);
      setStates(mappedStates);
    };

    useEffect(() => {
      if (typeof fetchRef.current !== "function") return;

      let cancelled = false;
      setIsFetching(true);

      (async () => {
        try {
          const regions = await fetchRef.current();
          if (!cancelled)
            applySavedRegions(Array.isArray(regions) ? regions : []);
        } catch (error) {
          console.error("Failed to load regions:", error);
        } finally {
          if (!cancelled) setIsFetching(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [fetchKey]);

    useLayoutEffect(() => {
      if (!data) return;

      // Edit Mode
      if (Array.isArray(data)) {
        applySavedRegions(data);
        return;
      }

      // Add Mode
      setZones(data.zones || []);
      setStates(data.states || initialStates);
    }, [data]);

    const updateZoneData = (updatedZones, updatedStates) => {
      setZones(updatedZones);

      setStates(updatedStates);

      if (onChange) {
        onChange({
          zones: updatedZones,
          states: updatedStates,
        });
      }
    };
    useImperativeHandle(ref, () => ({
      validate() {
        if (zones.length === 0) {
          Swal.fire({
            icon: "error",
            text: "Please create at least one zone.",
          });

          return false;
        }

        const hasAssignedState = states.some((item) => item.zoneId !== null);

        if (!hasAssignedState) {
          Swal.fire({
            icon: "error",
            text: "Assign at least one state to a zone.",
          });

          return false;
        }

        return true;
      },

      getPayload() {
        return buildRegionPayload(zones, states);
      },
    }));
    const filteredStates = useMemo(() => {
      return states.filter((item) =>
        item?.name?.toLowerCase().includes(search?.toLowerCase()),
      );
    }, [search, states]);

    const handleSelectState = (state) => {
      setSelectedState(state);
    };

    // ================= DEFAULT SETUP =================
    const handleLoadDefaultSetup = async () => {
      if (zones.length > 0) {
        const result = await Swal.fire({
          title: "Replace current zones?",
          text: "Loading the default setup will replace the zones and assignments currently shown.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Load Default Setup",
          cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;
      }

      const defaultZones = defaultZoneSetup.map((zone, index) => ({
        id: `${DEFAULT_ZONE_PREFIX}${index + 1}`,
        name: zone.name,
        color: zone.color,
      }));

      const zoneIdsByState = new Map(
        defaultZoneSetup.flatMap((zone, index) =>
          zone.states.map((stateName) => [stateName, defaultZones[index].id]),
        ),
      );

      const defaultStates = initialStates.map((state) => ({
        ...state,
        zoneId: zoneIdsByState.get(state.name) || null,
      }));

      updateZoneData(defaultZones, defaultStates);
    };

    const handleAssignZone = (stateId, zoneId) => {
      const updatedStates = states.map((item) =>
        item.id === stateId ? { ...item, zoneId } : item,
      );

      updateZoneData(zones, updatedStates);
    };

    const handleAddZone = () => {
      setEditingZone(null);
      setOpenZoneDialog(true);
    };

    const handleEditZone = (zone) => {
      setEditingZone(zone);
      setOpenZoneDialog(true);
    };

    const handleCloseZoneDialog = () => {
      setEditingZone(null);
      setOpenZoneDialog(false);
    };

    const handleDeleteZone = async (zone) => {
      const result = await Swal.fire({
        title: "Delete Zone?",
        text: "All assigned states will become Unassigned.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Delete",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        reverseButtons: true,
      });

      if (!result.isConfirmed) return;

      const updatedZones = zones.filter((item) => item.id !== zone.id);

      // Make states of the deleted zone Unassigned
      const updatedStates = states.map((item) =>
        item.zoneId === zone.id ? { ...item, zoneId: null } : item,
      );

      updateZoneData(updatedZones, updatedStates);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Zone deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });
    };
    const isLoadingData = loading || isFetching;

    return (
      <Box
        sx={{
          p: 2,
          background: "#F5F7FB",
          minHeight: "100vh",
        }}
      >
        <Typography variant="h5" fontWeight={700} mb={3}>
          Zone Management
        </Typography>

        <Grid container spacing={3}>
          {/* ================= LEFT ================= */}

          <Grid item xs={12} md={3}>
            <StateSidebar
              states={filteredStates}
              search={search}
              setSearch={setSearch}
              selectedState={selectedState}
              onSelectState={handleSelectState}
              zones={zones}
            />
          </Grid>

          {/* ================= CENTER ================= */}

          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
              }}
            >
              {isLoadingData ? (
                <Box
                  sx={{
                    minHeight: 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                    sx={{ minHeight: 32 }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        fontSize={16}
                        fontWeight={600}
                        color="var(--all-black)"
                        sx={{ lineHeight: 1 }}
                      >
                        Zones
                      </Typography>

                      <Tooltip
                        title="A state can belong to only one zone."
                        arrow
                        placement="right"
                      >
                        <InfoOutlined
                          sx={{
                            fontSize: 16,
                            cursor: "pointer",
                            color: "var(--all-black) !important",
                            "& path": {
                              fill: "var(--all-black) !important",
                              stroke: "var(--all-black) !important",
                            },
                          }}
                        />
                      </Tooltip>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      {/* Hidden in edit mode so saved zones aren't replaced by defaults */}
                      {zones.length === 0 && (
                        <GlobalButton
                          size="small"
                          variant="secondary"
                          onClick={handleLoadDefaultSetup}
                        >
                          Use Default Setup
                        </GlobalButton>
                      )}

                      <Box
                        onClick={handleAddZone}
                        sx={{
                          height: 32,
                          boxSizing: "border-box",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.75,
                          cursor: "pointer",
                          px: 1.5,
                          border: "1.5px dashed #D9D9D9",
                          bgcolor: "#FAFAFA",
                        }}
                      >
                        <AddIcon
                          sx={{
                            fontSize: 24,
                            color: "var(--all-black) !important",
                            "& path": {
                              fill: "var(--all-black) !important",
                            },
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: "var(--all-black) !important",
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Add Other Zone
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid #E5E7EB",
                      margin: "0 0 24px 0",
                    }}
                  />

                  {zones.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        border: "2px dashed #D9D9D9",
                        bgcolor: "#FAFAFA",

                        borderRadius: 3,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "400px",
                        padding: "16px",
                      }}
                    >
                      <Typography variant="h6" fontWeight={600} mb={1}>
                        No Zone Created
                      </Typography>

                      <Typography
                        color="text.secondary"
                        mb={3}
                        textAlign="center"
                      >
                        Create your first zone, or start from the default setup
                        and edit it as needed.
                      </Typography>
                    </Paper>
                  ) : (
                    <Grid container spacing={2}>
                      {zones.map((zone) => (
                        <Grid item xs={12} md={6} key={zone.id}>
                          <ZoneCard
                            zone={zone}
                            states={states}
                            onAssign={handleAssignZone}
                            onRemove={handleAssignZone}
                            onEdit={handleEditZone}
                            onDelete={handleDeleteZone}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}
            </Paper>
          </Grid>

          {/* ================= RIGHT ================= */}

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
              }}
            >
              <Box
                sx={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography
                  fontSize={16}
                  fontWeight={600}
                  color="var(--all-black)"
                  sx={{ lineHeight: 1 }}
                >
                  Interactive Map
                </Typography>
              </Box>

              <IndiaMap
                states={states}
                zones={zones}
                selectedState={selectedState}
                onMarkerClick={handleSelectState}
              />
              <hr />

              <Box mt={3} className="flex justify-around items-center">
                {zones.map((zone) => (
                  <Box key={zone.id} display="flex" alignItems="center" mb={1}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: zone.color,
                        mr: 1,
                      }}
                    />

                    <Typography fontSize={14}>{zone.name}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
        <AddEditZoneDialog
          open={openZoneDialog}
          onClose={handleCloseZoneDialog}
          editingZone={editingZone}
          zones={zones}
          setZones={(updatedZones) => updateZoneData(updatedZones, states)}
        />
      </Box>
    );
  },
);
export default ZoneManagement;
